import { Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Clock, TrendingUp, ArrowRight, Newspaper, Flame, Zap, FileText, Star, ExternalLink, AlertTriangle } from "lucide-react";
import { useArticlesByCategory, Article } from "@/hooks/useArticles";
import { formatDistanceToNow } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const CATEGORIES = [
  { id: 'daily-briefing', label: 'Daily Briefing', icon: Newspaper },
  { id: 'breaking', label: 'Breaking', icon: Flame },
  { id: 'market-flash', label: 'Market Flash', icon: Zap },
  { id: 'analysis', label: 'Analysis', icon: FileText },
  { id: 'deep-dive', label: 'Deep Dives', icon: Star },
  { id: 'latest-updates', label: 'Latest Updates', icon: ExternalLink },
];

interface ArticleCardProps {
  article: Article;
}

const ArticleCard = ({ article }: ArticleCardProps) => {
  const timeAgo = article.publish_date 
    ? formatDistanceToNow(new Date(article.publish_date), { addSuffix: true })
    : 'Recently';

  return (
    <Link to={`/articles/${article.slug}`} className="group block">
      <Card className="h-full overflow-hidden border-border/50 bg-card/80 hover:border-primary/50 hover:bg-card transition-all duration-300">
        <CardContent className="p-4">
          <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
            {article.headline}
          </h4>
          
          {article.deck && (
            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
              {article.deck}
            </p>
          )}
          
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock className="w-3 h-3" />
              {timeAgo}
            </span>
            <span>{article.reading_time || 5} min</span>
            {article.opportunity_score && (
              <span className="flex items-center gap-1 text-green-500">
                <TrendingUp className="w-3 h-3" />
                {article.opportunity_score}
              </span>
            )}
          </div>
          
          {article.tokens_featured && article.tokens_featured.length > 0 && (
            <div className="flex gap-1 mt-3 flex-wrap">
              {article.tokens_featured.slice(0, 2).map((token) => (
                <Badge key={token} variant="secondary" className="text-xs">
                  {token}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  );
};

interface CategoryContentProps {
  category: string;
}

const CategoryContent = ({ category }: CategoryContentProps) => {
  const { data: articles, isLoading } = useArticlesByCategory(category, 3);

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[180px] rounded-xl" />
        ))}
      </div>
    );
  }

  if (!articles || articles.length === 0) {
    return (
      <Card className="border-dashed border-border/50 bg-card/50">
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">No articles in this category yet.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      {articles.map((article) => (
        <ArticleCard key={article.id} article={article} />
      ))}
    </div>
  );
};

// Latest Updates tab content - external news feed
const LatestUpdatesContent = () => {
  const { data: newsItems, isLoading } = useQuery({
    queryKey: ['news-feed-tab'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('news_feed')
        .select('*')
        .order('collected_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return data;
    },
    refetchInterval: 5 * 60 * 1000,
  });

  // Also fetch recent market flash articles
  const { data: flashArticles } = useQuery({
    queryKey: ['market-flash-articles-tab'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('articles')
        .select('id, slug, headline, summary, publish_date, category')
        .eq('category', 'market-flash')
        .eq('status', 'published')
        .order('publish_date', { ascending: false })
        .limit(5);

      if (error) throw error;
      return data;
    },
    refetchInterval: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-[180px] rounded-xl" />
        ))}
      </div>
    );
  }

  const categoryConfig: Record<string, { icon: React.ReactNode; color: string; label: string }> = {
    'market-flash': { icon: <Zap className="w-3 h-3" />, color: 'bg-yellow-500/20 text-yellow-500', label: 'Market Flash' },
    'breaking': { icon: <AlertTriangle className="w-3 h-3" />, color: 'bg-red-500/20 text-red-500', label: 'Breaking' },
    'analysis': { icon: <TrendingUp className="w-3 h-3" />, color: 'bg-blue-500/20 text-blue-500', label: 'Analysis' },
    'default': { icon: <Newspaper className="w-3 h-3" />, color: 'bg-muted text-muted-foreground', label: 'News' },
  };

  // Combine news feed with flash articles
  const combinedItems = [
    ...(flashArticles?.map(article => ({
      id: article.id,
      type: 'article' as const,
      headline: article.headline,
      summary: article.summary,
      slug: article.slug,
      publishedAt: article.publish_date,
      category: article.category,
    })) || []),
    ...(newsItems?.map(news => ({
      id: news.id,
      type: 'news' as const,
      headline: news.headline,
      summary: news.summary,
      source: news.source,
      sourceUrl: news.source_url,
      publishedAt: news.collected_at,
      category: news.category,
      tokens: news.tokens_mentioned,
    })) || []),
  ].sort((a, b) => new Date(b.publishedAt || '').getTime() - new Date(a.publishedAt || '').getTime())
   .slice(0, 6);

  if (combinedItems.length === 0) {
    return (
      <Card className="border-dashed border-border/50 bg-card/50">
        <CardContent className="p-8 text-center">
          <p className="text-muted-foreground">No recent updates. Check back soon for market flashes and news.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {combinedItems.map((item) => {
        const config = categoryConfig[item.category || 'default'] || categoryConfig.default;
        
        return (
          <Card key={item.id} className="h-full overflow-hidden border-border/50 bg-card/80 hover:border-primary/50 hover:bg-card transition-all duration-300 group">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <div className={`p-1 rounded ${config.color}`}>
                  {config.icon}
                </div>
                <Badge variant="secondary" className="text-xs">
                  {config.label}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {item.publishedAt ? formatDistanceToNow(new Date(item.publishedAt), { addSuffix: true }) : 'Recently'}
                </span>
              </div>
              
              {item.type === 'article' ? (
                <Link to={`/articles/${item.slug}`} className="block">
                  <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2">
                    {item.headline}
                  </h4>
                </Link>
              ) : (
                <a
                  href={item.sourceUrl || '#'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block"
                >
                  <h4 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2 mb-2 flex items-center gap-1">
                    {item.headline}
                    {item.sourceUrl && <ExternalLink className="w-3 h-3 flex-shrink-0" />}
                  </h4>
                </a>
              )}
              
              {item.summary && (
                <p className="text-sm text-muted-foreground line-clamp-2">
                  {item.summary}
                </p>
              )}
              
              {item.type === 'news' && item.tokens && item.tokens.length > 0 && (
                <div className="flex gap-1 mt-3 flex-wrap">
                  {item.tokens.slice(0, 2).map((token) => (
                    <Badge key={token} variant="outline" className="text-xs">
                      {token}
                    </Badge>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

export const CategoryNewsSection = () => {
  return (
    <section className="container mx-auto px-4 py-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-2 h-8 bg-primary rounded-full" />
          <h2 className="text-2xl font-bold text-foreground">Browse by Category</h2>
        </div>
        <Link to="/articles">
          <Button variant="ghost" size="sm" className="gap-2 text-muted-foreground hover:text-foreground">
            View All
            <ArrowRight className="w-4 h-4" />
          </Button>
        </Link>
      </div>

      <Tabs defaultValue="daily-briefing" className="w-full">
        <TabsList className="w-full justify-start mb-6 bg-muted/50 p-1 h-auto flex-wrap">
          {CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <TabsTrigger
                key={cat.id}
                value={cat.id}
                className="gap-2 data-[state=active]:bg-background data-[state=active]:text-foreground px-4 py-2"
              >
                <Icon className="w-4 h-4" />
                <span className="hidden sm:inline">{cat.label}</span>
              </TabsTrigger>
            );
          })}
        </TabsList>

        {CATEGORIES.map((cat) => (
          <TabsContent key={cat.id} value={cat.id} className="mt-0">
            {cat.id === 'latest-updates' ? (
              <LatestUpdatesContent />
            ) : (
              <CategoryContent category={cat.id} />
            )}
          </TabsContent>
        ))}
      </Tabs>
    </section>
  );
};
